/**
 * Create Client Dialog
 * Modal dialog for creating new clients directly
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
  Box,
  Typography,
  Chip,
  IconButton
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon
} from '@mui/icons-material';
import type { ClientType } from '../types/client';

export interface CreateClientFormData {
  clientName: string;
  clientType: ClientType;
  primaryContact: string;
  email: string;
  phone: string;
  industry: string;
  companySize: string;
  website: string;
  businessAddress: string;
  tags: string[];
}

interface CreateClientDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateClientFormData) => void;
  isLoading?: boolean;
}

const CreateClientDialog: React.FC<CreateClientDialogProps> = ({
  open,
  onClose,
  onSubmit,
  isLoading = false
}) => {
  const [formData, setFormData] = useState<CreateClientFormData>({
    clientName: '',
    clientType: 'INDIVIDUAL',
    primaryContact: '',
    email: '',
    phone: '',
    industry: '',
    companySize: '',
    website: '',
    businessAddress: '',
    tags: []
  });

  const [tagInput, setTagInput] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (field: keyof CreateClientFormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const handleDeleteTag = (tagToDelete: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToDelete)
    }));
  };

  const handleTagInputKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAddTag();
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.clientName.trim()) {
      newErrors.clientName = 'Client name is required';
    }

    if (!formData.primaryContact.trim()) {
      newErrors.primaryContact = 'Primary contact is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      onSubmit(formData);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setFormData({
        clientName: '',
        clientType: 'INDIVIDUAL',
        primaryContact: '',
        email: '',
        phone: '',
        industry: '',
        companySize: '',
        website: '',
        businessAddress: '',
        tags: []
      });
      setTagInput('');
      setErrors({});
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '600px' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" component="div">
          Create New Client
        </Typography>
        <IconButton onClick={handleClose} disabled={isLoading} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={2.5}>
          {/* Client Information */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'primary.main' }}>
              Client Information
            </Typography>
          </Grid>

          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              required
              label="Client Name"
              value={formData.clientName}
              onChange={handleChange('clientName')}
              error={!!errors.clientName}
              helperText={errors.clientName}
              disabled={isLoading}
              placeholder="e.g., Acme Corporation"
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              select
              required
              label="Client Type"
              value={formData.clientType}
              onChange={handleChange('clientType')}
              disabled={isLoading}
            >
              <MenuItem value="INDIVIDUAL">Individual</MenuItem>
              <MenuItem value="SMALL_BUSINESS">Small Business</MenuItem>
              <MenuItem value="ENTERPRISE">Enterprise</MenuItem>
              <MenuItem value="GOVERNMENT">Government</MenuItem>
              <MenuItem value="NON_PROFIT">Non-Profit</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Industry"
              value={formData.industry}
              onChange={handleChange('industry')}
              disabled={isLoading}
              placeholder="e.g., Technology, Manufacturing"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Company Size"
              value={formData.companySize}
              onChange={handleChange('companySize')}
              disabled={isLoading}
              placeholder="e.g., 10-50, 50-200, 200+"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Website"
              value={formData.website}
              onChange={handleChange('website')}
              disabled={isLoading}
              placeholder="https://example.com"
            />
          </Grid>

          {/* Contact Information */}
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'primary.main' }}>
              Contact Information
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="Primary Contact Name"
              value={formData.primaryContact}
              onChange={handleChange('primaryContact')}
              error={!!errors.primaryContact}
              helperText={errors.primaryContact}
              disabled={isLoading}
              placeholder="John Doe"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="Email"
              type="email"
              value={formData.email}
              onChange={handleChange('email')}
              error={!!errors.email}
              helperText={errors.email}
              disabled={isLoading}
              placeholder="john.doe@example.com"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Phone"
              value={formData.phone}
              onChange={handleChange('phone')}
              disabled={isLoading}
              placeholder="+60 12-345 6789"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Business Address"
              value={formData.businessAddress}
              onChange={handleChange('businessAddress')}
              disabled={isLoading}
              placeholder="123 Business St, City"
            />
          </Grid>

          {/* Tags */}
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'primary.main' }}>
              Tags (Optional)
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField
                fullWidth
                size="small"
                label="Add Tag"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={handleTagInputKeyPress}
                disabled={isLoading}
                placeholder="e.g., VIP, High Priority"
              />
              <Button
                variant="outlined"
                onClick={handleAddTag}
                disabled={isLoading || !tagInput.trim()}
                startIcon={<AddIcon />}
              >
                Add
              </Button>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {formData.tags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  onDelete={() => handleDeleteTag(tag)}
                  disabled={isLoading}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              ))}
              {formData.tags.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  No tags added
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isLoading}
        >
          {isLoading ? 'Creating...' : 'Create Client'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateClientDialog;
